import { useAuth } from '@/app/context';
import {
  Field,
  FilterCombobox,
} from '@/features/reports/components/shared/filter-combobox.component';
import {
  useAdvisoryOptions,
  useBrokerOptions,
} from '@/features/reports/hooks/use-reports-filter-options.hook';
import { DateRangeFilterField } from '@/shared/ui';
import { Button } from '@noorden-insurtech/alba-design-system';
import { useState } from 'react';
import { toaster } from '../../../shared/ui/toaster.component';
import { useCommissionStatementPrint } from '../hooks';

/** Janela máxima do Período (regra de negócio). */
const MAX_PERIOD_MONTHS = 12;

const todayIso = (): string => {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
};

/** Subtrai `n` meses de uma data ISO yyyy-MM-dd (clamp no último dia do mês). */
function subtractMonthsIso(iso: string, n: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  const base = new Date(year, month - 1 - n, 1);
  const lastDay = new Date(
    base.getFullYear(),
    base.getMonth() + 1,
    0
  ).getDate();
  const result = new Date(
    base.getFullYear(),
    base.getMonth(),
    Math.min(day, lastDay)
  );
  const mm = String(result.getMonth() + 1).padStart(2, '0');
  const dd = String(result.getDate()).padStart(2, '0');
  return `${result.getFullYear()}-${mm}-${dd}`;
}

/** Nº de meses (bruto) entre duas datas ISO yyyy-MM-dd. */
function monthsBetweenDates(from: string, to: string): number {
  const [fromYear, fromMonth, fromDay] = from.split('-').map(Number);
  const [toYear, toMonth, toDay] = to.split('-').map(Number);
  let months = (toYear - fromYear) * 12 + (toMonth - fromMonth);
  if (toDay < fromDay) months -= 1;
  return months;
}

export function CommissionStatementTab() {
  const { groups } = useAuth();
  const isInsurer = (groups ?? []).some(group => group.startsWith('insurer.'));
  const isAdvisory = (groups ?? []).some(group =>
    group.startsWith('advisory.')
  );

  const [advisory, setAdvisory] = useState('');
  const [broker, setBroker] = useState('');
  // Período pré-preenchido com o último mês: [hoje - 1 mês, hoje].
  const [dateFrom, setDateFrom] = useState(() =>
    subtractMonthsIso(todayIso(), 1)
  );
  const [dateTo, setDateTo] = useState(() => todayIso());
  const [errors, setErrors] = useState<{
    period?: string;
    favorecido?: string;
  }>({});

  // Mesmo gating do "Meus Recibos": Assessoria só para Seguradora; Corretor para
  // Seguradora + Assessoria. Corretor/usuário veem só o período (escopo no backend).
  const showAdvisory = isInsurer;
  const showBroker = isInsurer || isAdvisory;
  const { data: advisoryOptions = [] } = useAdvisoryOptions(showAdvisory);
  const { data: brokerOptions = [] } = useBrokerOptions(showBroker);

  const print = useCommissionStatementPrint();

  const handlePrint = () => {
    const nextErrors: { period?: string; favorecido?: string } = {};

    // Seguradora precisa escolher um favorecido (assessoria ou corretor).
    if (isInsurer && !advisory && !broker) {
      nextErrors.favorecido = 'Selecione a assessoria ou o corretor.';
    }

    if (!dateFrom || !dateTo) {
      nextErrors.period = 'O período é obrigatório.';
    } else if (dateFrom > todayIso()) {
      nextErrors.period = 'O início não pode ser maior que a data atual.';
    } else if (dateTo < dateFrom) {
      nextErrors.period = 'O fim não pode ser menor que o início.';
    }

    setErrors(nextErrors);
    if (nextErrors.favorecido || nextErrors.period) {
      toaster.create({
        title: 'Verifique os filtros',
        description: nextErrors.favorecido ?? nextErrors.period,
        type: 'error',
      });
      return;
    }

    if (monthsBetweenDates(dateFrom, dateTo) > MAX_PERIOD_MONTHS) {
      toaster.create({
        title: 'Período',
        description: 'O período não pode ultrapassar 12 meses.',
        type: 'error',
      });
      return;
    }

    print.mutate(
      {
        // Tipo inferido pela seleção; o backend resolve o favorecido pelo escopo.
        beneficiaryType: broker ? 2 : advisory ? 1 : undefined,
        advisoryPartyCode: advisory || undefined,
        brokerPartyCode: broker || undefined,
        dateFrom,
        dateTo,
      },
      {
        onError: error =>
          toaster.create({
            title: 'Imprimir Relatório',
            description:
              error instanceof Error
                ? error.message
                : 'Não foi possível imprimir o Demonstrativo de Comissão no momento. Tente novamente mais tarde.',
            type: 'error',
          }),
      }
    );
  };

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex flex-col gap-1'>
        <h2 className='font-montserrat text-lg text-alba-black-500'>
          Impressão de Extrato
        </h2>
        <p className='text-sm text-alba-gray-600'>
          Informe o relatório e, em seguida, os filtros necessários para gerar
          os dados.
        </p>
      </div>

      <div className='rounded-3xl bg-[#E7E8E9]/50 px-6 py-4'>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
          {showAdvisory && (
            <Field label='Assessoria'>
              <FilterCombobox
                placeholder='Selecione a assessoria'
                options={advisoryOptions}
                value={advisory}
                onChange={setAdvisory}
              />
            </Field>
          )}

          {showBroker && (
            <Field label='Corretor'>
              <FilterCombobox
                placeholder='Selecione o corretor'
                options={brokerOptions}
                value={broker}
                onChange={setBroker}
              />
            </Field>
          )}

          <div className='flex flex-col gap-1.5'>
            <Field label='Período *'>
              <DateRangeFilterField
                from={dateFrom}
                to={dateTo}
                placeholder='Período'
                onChange={range => {
                  setDateFrom(range.from);
                  setDateTo(range.to);
                }}
              />
            </Field>
            {errors.period && (
              <span className='text-xs text-alba-error-red-500'>
                {errors.period}
              </span>
            )}
          </div>
        </div>

        {errors.favorecido && (
          <span className='mt-2 block text-xs text-alba-error-red-500'>
            {errors.favorecido}
          </span>
        )}
      </div>

      <div>
        <Button
          variant='primary'
          size='md'
          loading={print.isPending}
          onClick={handlePrint}
        >
          Imprimir Relatório
        </Button>
      </div>
    </div>
  );
}
